import { refreshStyleManager } from './styleManager';
import { applyBackendVariables, injectVariablesToCanvas } from './backendAdapter';
import { TYPE_COLOR, TYPE_SIZE } from './types';

export { getDevices } from './breakpoints';

/**
 * Build the full CSS variable name: --{name}
 */
function buildVarName(name) {
    return `--${name}`;
}

/**
 * Build a var() reference string
 */
export function buildVarRef(name) {
    return `var(${buildVarName(name)})`;
}

/**
 * Get all managed variables (base / large-desktop values from backend store).
 */
export function getVariables(editor) {
    const store = editor.getModel().get('cssVarStore');
    if (!store) return { colors: [], sizes: [] };

    const colors = [];
    const sizes = [];

    for (const { type, name } of store.order) {
        const value = store.baseValues[name];
        if (!value) continue;
        const entry = { name, value, fullName: `--${name}` };
        if (type === TYPE_COLOR) {
            colors.push(entry);
        } else if (type === TYPE_SIZE) {
            sizes.push(entry);
        }
    }

    return { colors, sizes };
}

/**
 * Resolve the rule the user is currently editing in the Style Manager.
 */
function getActiveStyleTarget(editor) {
    const sel = editor.StyleManager.getSelected();
    if (sel) return sel;
    const comp = editor.getSelected();
    return comp ? editor.StyleManager.getModelToStyle(comp) : null;
}

/**
 * Get the current CSS value for a property from the style target.
 */
export function getTargetStyleValue(editor, property) {
    const propName = property.get ? property.get('property') : '';
    if (!propName) return '';
    const target = getActiveStyleTarget(editor);
    if (!target) return '';
    const style = target.getStyle();
    return String(style[propName] || '');
}

/**
 * Set a CSS property value directly on the style target.
 */
export function setTargetStyleValue(editor, property, value) {
    const propName = property.get ? property.get('property') : '';
    if (!propName) return;
    const target = getActiveStyleTarget(editor);
    if (!target) return;
    const style = { ...target.getStyle() };
    if (value) {
        style[propName] = value;
    } else {
        delete style[propName];
    }
    target.setStyle(style);
}

/**
 * Initialize CSS variables from backend data and keep them in sync on load events.
 */
export function initVariables(editor, options) {
    const { variables = [] } = options;

    const applyVariables = () => {
        applyBackendVariables(editor, variables);
    };

    let debouncedRefreshTimeout;
    const debouncedRefresh = () => {
        clearTimeout(debouncedRefreshTimeout);
        debouncedRefreshTimeout = setTimeout(() => {
            applyVariables();
            refreshStyleManager(editor, options);
        }, 50);
    };

    editor.on('storage:end:load', () => {
        debouncedRefresh();
    });

    editor.on('canvas:frame:load', () => {
        injectVariablesToCanvas(editor);
    });

    editor.on('page', () => {
        debouncedRefresh();
    });

    editor.on('undo', debouncedRefresh);
    editor.on('redo', debouncedRefresh);

    editor.on('load', () => {
        applyVariables();
    });
}
